import React, { useLayoutEffect, useRef } from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import { markNativeWebViewIfNeeded } from '../../util/seaticsPrecheckoutWebViewFix';

import css from './ListingRewardsEarnBanner.module.css';

// Client import path: mark before first paint when possible (attendee WebView only).
if (typeof window !== 'undefined') {
  markNativeWebViewIfNeeded();
}

export const LISTING_REWARDS_BANNER_ATTR = 'data-listing-rewards-banner';
export const LISTING_REWARDS_BANNER_FLOW_ATTR = 'data-listing-rewards-banner-flow';
export const LISTING_STICKY_HEADER_ATTR = 'data-listing-sticky-header';

/** Published on <html> from the sticky header's measured height (useLayoutEffect). */
export const LISTING_STICKY_CHROME_HEIGHT_VAR = '--listingStickyChromeHeight';

/**
 * Thin conversion strip.
 *
 * Desktop: sticky under the PromoTix topbar.
 * Mobile Safari: in-flow above the map, stuck under the topbar (same visual
 * stack as the native app: header → banner → Seatics).
 * Native WebView: in-flow above the map (unchanged).
 */
const ListingRewardsEarnBanner = ({ placement = 'sticky' } = {}) => {
  const isFlow = placement === 'flow';
  const attrs = isFlow
    ? { [LISTING_REWARDS_BANNER_FLOW_ATTR]: '1' }
    : { [LISTING_REWARDS_BANNER_ATTR]: '1' };
  return (
    <div className={isFlow ? css.flowRoot : css.root} role="note" {...attrs}>
      <p className={css.textMobile}>
        <FormattedMessage id="ListingPage.rewardsEarnBannerMobile" />
      </p>
      <p className={css.textDesktop}>
        <FormattedMessage id="ListingPage.rewardsEarnBanner" />
      </p>
    </div>
  );
};

/** Sticky listing chrome: marketplace topbar + earn banner as one block. */
export const listingPageStickyTopbar = topbar => (
  <ListingStickyHeaderWithNativeMark topbar={topbar} />
);

const publishListingStickyChromeHeight = el => {
  if (typeof document === 'undefined' || !(el instanceof HTMLElement)) return;
  const height = Math.max(0, Math.ceil(el.getBoundingClientRect().height));
  if (!height) return;
  document.documentElement.style.setProperty(LISTING_STICKY_CHROME_HEIGHT_VAR, `${height}px`);
};

const ListingStickyHeaderWithNativeMark = ({ topbar }) => {
  const stickyRef = useRef(null);

  useLayoutEffect(() => {
    markNativeWebViewIfNeeded();
  }, []);

  useLayoutEffect(() => {
    const el = stickyRef.current;
    if (!el) return undefined;

    const sync = () => publishListingStickyChromeHeight(el);
    sync();

    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(sync);
      ro.observe(el);
    }
    window.addEventListener('resize', sync);
    window.addEventListener('orientationchange', sync);

    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', sync);
      window.removeEventListener('orientationchange', sync);
    };
  }, []);

  return (
    <div ref={stickyRef} {...{ [LISTING_STICKY_HEADER_ATTR]: '1' }}>
      {topbar}
      <ListingRewardsEarnBanner placement="sticky" />
    </div>
  );
};

export default ListingRewardsEarnBanner;
